'use client';

import { cn, Icon } from '@bridebook/aura';
import { forwardRef, type ComponentPropsWithoutRef, ElementRef, ComponentProps } from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { Check } from '@bridebook/aura/src/icons';
import { checkboxIconSize, checkboxVariants, type CheckboxSize } from './checkbox-variants';

export type CheckboxProps = ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> & {
  size?: CheckboxSize;
  error?: boolean;
};

export type CheckboxOnChange = NonNullable<ComponentProps<typeof Checkbox>['onCheckedChange']>;
export type CheckboxCheckedState = CheckboxPrimitive.CheckedState;

export const Checkbox = forwardRef<ElementRef<typeof CheckboxPrimitive.Root>, CheckboxProps>(
  ({ className, size = 'standard', disabled, error = false, id, ...props }, ref) => (
    <CheckboxPrimitive.Root
      data-slot="checkbox"
      ref={ref}
      className={cn(
        checkboxVariants({ size }),
        'peer outline-none focus-visible:ring-[3px] focus-visible:ring-focus-ring disabled:cursor-not-allowed',
        // Figma thickens the border on hover and error, and leaves a disabled box alone
        'not-data-disabled:hover:border-border-focus not-data-disabled:not-data-[state=checked]:hover:border-2',
        'aria-invalid:border-2 aria-invalid:border-feedback-error-accent aria-invalid:data-[state=checked]:not-data-disabled:border-feedback-error-accent',
        className,
      )}
      disabled={disabled}
      aria-invalid={error}
      id={id}
      {...props}>
      <CheckboxPrimitive.Indicator
        data-slot="checkbox-indicator"
        className="flex h-full items-center justify-center">
        <Icon size={checkboxIconSize[size]} icon={Check} className="stroke-2 text-text-inverse" />
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  ),
);
Checkbox.displayName = CheckboxPrimitive.Root.displayName;

export { checkboxIconSize, checkboxVariants, type CheckboxSize } from './checkbox-variants';
export { CheckboxIndicator, type CheckboxIndicatorProps } from './checkbox-indicator';
